/* RGB mobile orphan-X guard — V3 08 Oct 2026.
   Defensive recovery for third-party popup/spin-wheel controls restored from bfcache.
   Does not replace or disable the popup app. */
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  if (!mobile.matches) return;

  const HINT_RE = /(spin|wheel|popup.?boost|pb[-_]?spin|boost.?pop)/i;
  const CLOSE_RE = /^(x|×|✕|✖|close)$/i;
  const SAFE_SELECTOR = [
    'header',
    'menu-drawer',
    'cart-drawer',
    '#shopify-chat',
    '[id*="ShopifyChat" i]',
    '[id*="tidio" i]',
    '[class*="tidio" i]',
    '[class*="inbox" i]'
  ].join(',');

  let recoveringUntil = 0;
  let raf = 0;

  const visible = (el) => {
    if (!el || !document.documentElement.contains(el)) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return false;
    if (el.getAttribute('aria-hidden') === 'true' || el.hidden) return false;
    const r = el.getBoundingClientRect();
    return r.width > 2 && r.height > 2;
  };

  const signature = (el) => {
    if (!el) return '';
    return [
      el.id || '',
      typeof el.className === 'string' ? el.className : '',
      el.getAttribute?.('data-testid') || '',
      el.getAttribute?.('data-app') || '',
      el.getAttribute?.('title') || '',
      el.getAttribute?.('aria-label') || ''
    ].join(' ');
  };

  const hintedRoot = (el) => {
    let node = el;
    for (let i = 0; node && i < 9; i += 1, node = node.parentElement) {
      if (HINT_RE.test(signature(node))) return node;
    }
    return null;
  };

  const dialogRoot = (el) => el.closest?.(
    '[role="dialog"], dialog, [aria-modal="true"], [class*="modal" i], [class*="popup" i], [class*="overlay" i]'
  );

  const closeLike = (el) => {
    const label = [
      el.getAttribute?.('aria-label') || '',
      el.getAttribute?.('title') || '',
      (el.textContent || '').trim()
    ].join(' ').trim();
    return /close/i.test(label) || CLOSE_RE.test((el.textContent || '').trim());
  };

  const suppress = (el) => {
    if (!el || el.dataset.rgbOrphanXHidden === 'true') return;
    el.dataset.rgbOrphanXHidden = 'true';
    el.dataset.rgbOrphanXOpacity = el.style.getPropertyValue('opacity') || '';
    el.dataset.rgbOrphanXVisibility = el.style.getPropertyValue('visibility') || '';
    el.dataset.rgbOrphanXPointer = el.style.getPropertyValue('pointer-events') || '';
    el.style.setProperty('opacity', '0', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
  };

  const restore = (el) => {
    if (!el || el.dataset.rgbOrphanXHidden !== 'true') return;
    const restoreProp = (prop, key) => {
      const val = el.dataset[key] || '';
      if (val) el.style.setProperty(prop, val);
      else el.style.removeProperty(prop);
      delete el.dataset[key];
    };
    restoreProp('opacity', 'rgbOrphanXOpacity');
    restoreProp('visibility', 'rgbOrphanXVisibility');
    restoreProp('pointer-events', 'rgbOrphanXPointer');
    delete el.dataset.rgbOrphanXHidden;
  };

  const suppressFrame = (frame) => {
    if (frame.dataset.rgbOrphanFrameHidden === 'true') return;
    frame.dataset.rgbOrphanFrameHidden = 'true';
    frame.dataset.rgbOrphanFrameOpacity = frame.style.getPropertyValue('opacity') || '';
    frame.style.setProperty('opacity', '0', 'important');
    frame.style.setProperty('pointer-events', 'none', 'important');
  };

  const restoreFrame = (frame) => {
    if (frame.dataset.rgbOrphanFrameHidden !== 'true') return;
    const val = frame.dataset.rgbOrphanFrameOpacity || '';
    if (val) frame.style.setProperty('opacity', val);
    else frame.style.removeProperty('opacity');
    frame.style.removeProperty('pointer-events');
    delete frame.dataset.rgbOrphanFrameOpacity;
    delete frame.dataset.rgbOrphanFrameHidden;
  };

  const sweep = () => {
    raf = 0;
    if (!mobile.matches) return;

    const recovering = Date.now() < recoveringUntil;
    const candidates = document.querySelectorAll(
      'button, [role="button"], [aria-label*="close" i], [title*="close" i], [class*="close" i], [id*="close" i]'
    );

    candidates.forEach((el) => {
      if (!(el instanceof HTMLElement) || el.closest(SAFE_SELECTOR) || !closeLike(el)) return;

      const hinted = hintedRoot(el);
      const dialog = dialogRoot(el);
      const root = hinted || dialog;
      const cs = getComputedStyle(el);
      const z = Number.parseInt(cs.zIndex, 10) || 0;
      const r = el.getBoundingClientRect();
      const compactFloating = (cs.position === 'fixed' || cs.position === 'absolute') &&
        r.width <= 96 && r.height <= 96 && z >= 100;

      const orphaned = root ? !visible(root) : false;
      const recoveryRisk = recovering && (Boolean(hinted) || compactFloating);

      if (orphaned || recoveryRisk) suppress(el);
      else if (!recovering && (!root || visible(root))) restore(el);
    });

    document.querySelectorAll('iframe').forEach((frame) => {
      const sig = [
        frame.src || '',
        frame.title || '',
        signature(frame),
        signature(frame.parentElement)
      ].join(' ');
      if (!HINT_RE.test(sig)) return;
      if (recovering) suppressFrame(frame);
      else restoreFrame(frame);
    });
  };

  const scheduleSweep = () => {
    if (!raf) raf = requestAnimationFrame(sweep);
  };

  const beginRecovery = () => {
    recoveringUntil = Date.now() + 1200;
    scheduleSweep();
    [80, 220, 500, 900, 1300].forEach((ms) => window.setTimeout(scheduleSweep, ms));
  };

  window.addEventListener('pageshow', beginRecovery);
  window.addEventListener('popstate', beginRecovery);
  window.addEventListener('pagehide', scheduleSweep);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') beginRecovery();
  });

  const observer = new MutationObserver(scheduleSweep);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open']
  });

  beginRecovery();
})();